import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import PropTypes from 'prop-types';

import { BASENAME, LAYOUT_TYPES } from '../../../constants';
import Loading from '../../atoms/Loading';
import LineTitleGray from '../../atoms/titles/LineTitleGray';
import LinkButton from '../../atoms/button/LinkButton';
import Textlink from '../../atoms/link/Textlink';
import { setCurrentStep, setLayoutType, handleLogin, setErrorMessages } from '../../../modules/app';
import environments from '../../../environments';
import { getSessionId } from '../../../utils/localStorage';
import { makeSessionMember } from '../../../utils/input';
import { updateSession } from '../../../modules/session';
import { applyErrorStyle, getValidationErrorMessages, removeErrorStyle } from '../../../utils/error';
import { FDataFromSession } from '../../Head';

const sessionId = getSessionId();

const Login = ({ session }) => {
  const currentStep = 2;
  const dispatch = useDispatch();
  const { userId, setUserId, password, setPassword } = useLogin();
  const [fetching, setFetching] = useState(false);

  useEffect(() => {
    dispatch(
      setLayoutType(LAYOUT_TYPES.step)
    );
    dispatch(setCurrentStep(currentStep));

    return () => {
      dispatch(
        setLayoutType(LAYOUT_TYPES.default)
      );
    };
  }, []);

  const history = useHistory();

  const handleLoginButton = () => {
    setFetching(true);

    removeErrorStyle('pageLogin');

    const onSuccess = async (member) => {
      const memberSessionInput = makeSessionMember(member);
      const onDone = () => {
        setFetching(false);
        history.push('/entry_customer');
      };

      dispatch(updateSession(memberSessionInput, onDone, onDone));
    };

    const onFailed = (errors) => {
      applyErrorStyle(errors);

      let messages = getValidationErrorMessages(errors);
      if (!messages && errors && errors.length) {
        messages = errors.map((e => e.debugMessage));
      }

      dispatch(setErrorMessages(messages));
      setFetching(false);
    };

    handleLogin(userId, password, onSuccess, onFailed);
  };

  const handleBack = () => {
    history.goBack();
  };

  const backPageUrl = `${environments.API_BASE_URI}/login/callback`;
  const enrollmentSrc = environments.ENROLLMENT_SRC;

  return (
    <div id="pageLogin" className="pageLogin">
      <FDataFromSession session={session} step={currentStep} />
      <Loading show={fetching} hideProgressBar progressRate={100} />
      <LineTitleGray size="lg">会員認証</LineTitleGray>
      <div className="loginContainer">
        <div className="loginItem">
          <div className="loginItemBodyWrapper">
            <LineTitleGray size="sm" border="borderAll">会員登録がお済みのお客様</LineTitleGray>
            <div className="loginItemBody">
              <div className="formBlock">
                <label>ユーザーID/<br className="isPC" />メールアドレス</label>
                <div className="loginForm">
                  <input
                    id="input.loginID"
                    type="email"
                    placeholder="0123456789ABCDEFG"
                    value={userId}
                    onChange={(e) => setUserId(e.target.value)}
                  />
                </div>
              </div>
              <div className="formBlock">
                <label>パスワード</label>
                <div className="loginForm">
                  <input
                    id="input.password"
                    type="password"
                    placeholder="パスワードを入力してください"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                </div>
              </div>
              <div className="loginSup">
              ユーザーIDを忘れた方は
                <Textlink
                  to={environments.DP_USERID_RESET_URI}
                  outgoing
                >
                  こちら
                </Textlink>
              </div>
              <div className="loginSup password">
              パスワードを忘れた方は
                <Textlink
                  to={environments.DP_PASSWORD_RESET_URI}
                  outgoing
                >
                  こちら
                </Textlink>
              </div>
              <div className="loginButton">
                <LinkButton buttonType="ctaMin" onClick={handleLoginButton}>ログインして予約へ進む</LinkButton>
              </div>
            </div>
          </div>
        </div>
        <div className="loginItem notRegistration">
          <div className="loginItemBodyWrapper">
            <LineTitleGray size="sm" border="borderAll">会員登録がまだのお客様</LineTitleGray>
            <div className="loginItemBody">
              <div className="banner">
                <img src="https://www.nta.co.jp/images/bn_kaiinninsho/bnr_yoyaku.gif" alt="日本旅行会員になると" />
              </div>
              <div className="loginSup forgot">
                メールマガジンについて詳しくは
                <Textlink
                  to="https://www.nta.co.jp/mailmagazine/index.html"
                  outgoing
                >
                  こちら
                </Textlink>
              </div>
              <div className="loginButton">
                <form action={environments.DP_SIGNUP_URI} method="POST" acceptCharset="Shift_JIS">
                  <input hidden name="BACK_PAGE" value={backPageUrl} readOnly />
                  <input hidden name="SESSION_INFO" value={sessionId} readOnly />
                  <input hidden name="ENROLLMENT_SRC" value={enrollmentSrc} readOnly />
                  <LinkButton buttonType="ctaMin">会員登録しながら予約へ進む</LinkButton>
                </form>
                <div className="notRegistrationLink">
                  <Textlink to={`${BASENAME}/mail`}>会員登録しないで予約（ゲスト予約）へ進む</Textlink>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="backButton">
        <LinkButton buttonType="navigateMin" onClick={handleBack}>申込み内容確認に戻る</LinkButton>
      </div>
    </div>
  );
};

Login.propTypes = {
  session: PropTypes.object,
};

export default Login;

const useLogin = () => {
  const [userId, setUserId] = useState('');
  const [password, setPassword] = useState('');

  return { userId, setUserId, password, setPassword };
};
